
import React, { useMemo } from 'react';
import { ShieldCheck, BrainCircuit, Split, AlertCircle, Sparkles, Download } from 'lucide-react';
import { AuditDetails } from '../../../types';
import { exportComparisonToExcel } from '../../../utils/exportExcel';
import type { SemanticMatchResult } from '../../../services/agents/semanticMatchService';
import { parseMatchedRulesString, highlightTermsInText } from '../../../utils/highlightTerms';
import InlineCostBadge from '../../../components/commercial/InlineCostBadge';
import { isCommercial } from '../../../config/appMode';

interface Step4ReportProps {
    auditResult: AuditDetails;
    sourceSegments: string[];
    aiOutputSegments: string[];
    humanSegments: string[];
    fileName: string;
    matchedRules?: string;
    semanticMatchResult?: SemanticMatchResult;
    onBackToReview: () => void;
    onNewSession: () => void;
    onShowDebug: () => void;
    onReaudit?: () => void;
    isAuditing?: boolean;
    reauditCredits?: number | null;
}

const Step4Report: React.FC<Step4ReportProps> = ({
    auditResult,
    sourceSegments,
    aiOutputSegments,
    humanSegments,
    fileName,
    matchedRules: matchedRulesText,
    semanticMatchResult,
    onReaudit,
    isAuditing: isAuditingProp,
    reauditCredits
}) => {
    // Override: if auditResult already loaded (has score), we're done regardless of stale state
    const isAuditing = isAuditingProp && !auditResult?.overallScore;
    // Parse matched rules for highlighting
    const parsedRules = useMemo(() => parseMatchedRulesString(matchedRulesText || ''), [matchedRulesText]);
    const sourceTerms = useMemo(() => parsedRules.map(r => r.source), [parsedRules]);
    const targetTerms = useMemo(() => parsedRules.map(r => r.target), [parsedRules]);
    // Helper: lay dung human text tu match mapping thay vi theo index
    const getMatchedHumanText = (sourceIdx: number): string | null => {
        if (!semanticMatchResult?.mappings) return null;
        const mapping = semanticMatchResult.mappings.find(m => m.sourceIndex === sourceIdx);
        if (!mapping || mapping.isUnmatched) return null;
        return mapping.humanText || null;
    };
    const handleExport = () => {
        const auditScores = auditResult.segmentEvaluations?.map(e => e.score);
        exportComparisonToExcel({
            sourceSegments,
            aiSegments: aiOutputSegments,
            benchmarkSegments: humanSegments,
            auditScores,
            fileName,
        });
    };
    // Only show rows that have Source content - ignore extra Human segments
    const maxRows = sourceSegments.length;

    // Score thresholds for 0-10 scale
    const getScoreColor = (score: number) => {
        if (score >= 8) return 'bg-emerald-500';
        if (score >= 6) return 'bg-amber-500';
        return 'bg-red-500';
    };

    const getScoreTextColor = (score: number) => {
        if (score >= 8) return 'text-emerald-500';
        if (score >= 6) return 'text-amber-500';
        return 'text-red-500';
    };

    return (
        <div className="flex flex-col h-full animate-in zoom-in-95 duration-500 px-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-5 shrink-0">
                <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-black text-slate-800 flex items-center gap-3">
                        <ShieldCheck className="text-emerald-600 w-7 h-7" /> Báo cáo Audit
                    </h2>
                </div>
                <div className="flex gap-2">
                    {onReaudit && (
                        <button
                            onClick={onReaudit}
                            disabled={isAuditing}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border transition-colors whitespace-nowrap ${
                                isAuditing
                                    ? 'text-slate-400 bg-slate-50 border-slate-200 cursor-not-allowed'
                                    : 'text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-200'
                            }`}
                            title="Chạy lại Audit"
                        >
                            <svg className={`w-3.5 h-3.5 ${isAuditing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            {isAuditing ? 'Đang chạy...' : 'Audit lại'}
                            {!isAuditing && isCommercial && reauditCredits != null && (
                                <InlineCostBadge credits={reauditCredits} compact className="ml-1 bg-white/70 text-amber-900 border-amber-300/70" />
                            )}
                        </button>
                    )}
                    <button
                        onClick={handleExport}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition-colors shadow-sm shadow-emerald-200 whitespace-nowrap"
                    >
                        <Download className="w-3.5 h-3.5" />
                        Xuất Excel
                    </button>
                </div>
            </div>

            {/* Truncation Warning */}
            {auditResult.isTruncated && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                        <h4 className="text-sm font-bold text-amber-800 uppercase tracking-wide">Kết quả bị cắt bớt</h4>
                        <p className="text-sm text-amber-700 mt-1">
                            Báo cáo Audit này chưa hoàn tất do vượt quá giới hạn độ dài (Max Output Tokens).
                            Vui lòng tăng <strong>Max Output Tokens</strong> trong cài đặt (ví dụ: 16000 hoặc 32000) và chạy lại Audit.
                        </p>
                    </div>
                </div>
            )}

            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4">
                {/* 2-column summary: Left = Score + Prompt | Right = Terminology rules */}
                <div className="grid grid-cols-2 gap-4 items-stretch">
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-col">
                    {/* Top row: Overall score + sub-scores stacked */}
                    <div className="flex items-start gap-5 pb-4 border-b border-slate-100">
                        {/* Overall */}
                        <div className="shrink-0 w-24">
                            <div className={`text-5xl font-black tabular-nums leading-none ${getScoreTextColor(auditResult.overallScore)}`}>
                                {auditResult.overallScore}
                            </div>
                            <div className="w-full bg-slate-100 h-1.5 rounded-full mt-2">
                                <div className={`h-full rounded-full ${getScoreColor(auditResult.overallScore)}`} style={{ width: `${auditResult.overallScore * 10}%` }} />
                            </div>
                            <span className="text-[10px] text-slate-400 uppercase tracking-widest mt-1.5 block font-semibold">Tổng điểm</span>
                            {auditResult.criticalErrors && auditResult.criticalErrors.length > 0 ? (
                                <div className="mt-2 text-[11px] text-red-600 flex items-center gap-1 font-semibold">
                                    <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    {auditResult.criticalErrors.length} lỗi
                                </div>
                            ) : (
                                <div className="mt-2 text-[11px] text-emerald-600 flex items-center gap-1 font-semibold">
                                    <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Đạt
                                </div>
                            )}
                        </div>

                        {/* Sub-scores horizontal bars */}
                        <div className="flex-1 min-w-0 space-y-2.5 pt-1">
                            {[
                                { label: 'Chính xác', value: auditResult.componentScores?.accuracy || 0 },
                                { label: 'Thuật ngữ', value: auditResult.componentScores?.terminology || 0 },
                                { label: 'Văn phong', value: auditResult.componentScores?.tone || 0 }
                            ].map(item => (
                                <div key={item.label} className="flex items-center gap-3">
                                    <span className="text-[11px] font-semibold text-slate-500 w-20 shrink-0">{item.label}</span>
                                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                        <div className={`h-full rounded-full ${getScoreColor(item.value)}`} style={{ width: `${item.value * 10}%` }} />
                                    </div>
                                    <span className={`text-sm font-bold tabular-nums w-6 text-right ${getScoreTextColor(item.value)}`}>{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Analysis bullets full-width */}
                    <div className="pt-4 space-y-3 flex-1">
                            {[
                                { label: 'Chính xác', text: auditResult.accuracyAnalysis, color: 'blue' },
                                { label: 'Thuật ngữ', text: auditResult.terminologyAnalysis, color: 'amber' },
                                { label: 'Văn phong', text: auditResult.toneAnalysis, color: 'purple' }
                            ].map(item => {
                                // Format logic: Remove "9/10:" prefix, split by newlines or ", -"
                                const cleanText = (item.text || '').replace(/^[\d.]+\/10:\s*/, '').trim();
                                // Split by ", -" or "\n-" or just new lines if list
                                const points = cleanText
                                    .replace(/,\s*-/g, '\n-') // Normalize comma-dash to newline-dash
                                    .split('\n')
                                    .map(s => s.trim())
                                    .filter(Boolean);

                                return (
                                    <div key={item.label} className="grid grid-cols-[80px_1fr] gap-2">
                                        <span className={`text-[10px] uppercase font-bold text-${item.color}-600 mt-1`}>{item.label}</span>
                                        <div className="text-xs text-slate-700 leading-relaxed">
                                            {points.length > 0 ? (
                                                <ul className="list-none space-y-1">
                                                    {points.map((point, i) => {
                                                        // Clean point: remove leading dash or "-1" scores if user wants to hide points
                                                        const displayPoint = point.replace(/^-[\d.]*\s*/, '').replace(/^-/, '').trim();
                                                        return (
                                                            <li key={i} className="flex items-start gap-1.5">
                                                                <span className={`mt-1.5 w-1 h-1 rounded-full bg-${item.color}-400 shrink-0`} />
                                                                <span>{displayPoint}</span>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            ) : (
                                                <span className="text-slate-400 italic">Không có nhận xét</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                    {/* Sub-section: Prompt suggestion */}
                    {auditResult.suggestedPromptUpdate && (
                        <div className="mt-5 pt-5 border-t border-slate-100 flex items-start gap-3">
                            <div className="p-2 bg-indigo-100 rounded-lg shrink-0">
                                <Sparkles className="w-4 h-4 text-indigo-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h4 className="text-[10px] font-black text-indigo-700 uppercase tracking-widest mb-1">Đề xuất cải thiện prompt</h4>
                                <p className="text-sm text-indigo-900 leading-relaxed">
                                    {auditResult.suggestedPromptUpdate}
                                </p>
                            </div>
                        </div>
                    )}
                </div>{/* end LEFT card */}

                {/* RIGHT card: Terminology rules */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-col">
                    {/* Terminology rules */}
                    {auditResult.glossaryRules && auditResult.glossaryRules.length > 0 ? (
                        <div className="flex flex-col flex-1 min-h-0">
                            <div className="flex items-center justify-between mb-3 shrink-0">
                                <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-2">
                                    <Split className="w-3.5 h-3.5 text-blue-600" />
                                    Quy tắc thuật ngữ (AI phát hiện) · {auditResult.glossaryRules.length}
                                </h3>
                                <button
                                    onClick={() => {
                                        const rulesText = auditResult.glossaryRules.map(r => `${r.source} → ${r.target}`).join('\n');
                                        navigator.clipboard.writeText(rulesText);
                                    }}
                                    className="text-xs font-bold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-colors"
                                >
                                    Sao chép tất cả
                                </button>
                            </div>
                            <div className="space-y-2 flex-1 min-h-0 overflow-y-auto custom-scrollbar">
                                {auditResult.glossaryRules.map((rule, idx) => (
                                    <div key={idx} className={`text-xs p-2.5 rounded border flex items-start gap-2 ${rule.type === 'terminology' ? 'bg-blue-50 border-blue-100 text-blue-800' :
                                        rule.type === 'format' ? 'bg-amber-50 border-amber-100 text-amber-800' :
                                            'bg-purple-50 border-purple-100 text-purple-800'
                                        }`}>
                                        <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${rule.type === 'terminology' ? 'bg-blue-100 text-blue-700' :
                                            rule.type === 'format' ? 'bg-amber-100 text-amber-700' :
                                                'bg-purple-100 text-purple-700'
                                            }`}>{rule.type}</span>
                                        <div className="flex-1">
                                            <span className="font-bold">{rule.source}</span>
                                            <span className="mx-1.5 text-slate-400">→</span>
                                            <span className="font-bold">{rule.target}</span>
                                            {rule.reason && <span className="text-slate-500 ml-2">({rule.reason})</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-slate-400 italic text-sm">
                            Không có quy tắc thuật ngữ nào được phát hiện.
                        </div>
                    )}
                </div>{/* end RIGHT card */}
                </div>{/* end 2-col grid */}

                {/* Full 3-Column Manual Comparison Table */}
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                    <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
                        <span className="font-black uppercase tracking-widest text-xs text-slate-600 flex items-center gap-2">
                            <Split className="w-4 h-4" /> Báo Cáo Thẩm Định Chi Tiết
                        </span>
                    </div>

                    <div className="grid grid-cols-4 bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                        <div className="px-4 py-3 border-r border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500">Văn bản gốc</div>
                        <div className="px-4 py-3 border-r border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500">Bản dịch AI</div>
                        <div className="px-4 py-3 border-r border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500">Benchmark</div>
                        <div className="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-slate-500">Nhận xét &amp; đánh giá</div>
                    </div>

                    <div className="max-h-[800px] overflow-y-auto custom-scrollbar">
                        {Array.from({ length: maxRows }).map((_, idx) => {
                            const segMapping = auditResult.segmentMappings?.find(s => (s.sourceIndex || 0) === idx + 1);

                            // Luon dung semanticMatchResult de xac dinh benchmark text (tin cay hon segMapping.humanIndices)
                            const matchMapping = semanticMatchResult?.mappings?.find(m => m.sourceIndex === idx);
                            const isUnmatched = matchMapping?.isUnmatched ?? false;
                            const matchedHumanText = matchMapping && !matchMapping.isUnmatched ? matchMapping.humanText : null;
                            const matchedIndices = matchMapping && !matchMapping.isUnmatched ? matchMapping.allHumanIndices : [];
                            const isMultiMatch = matchedIndices.length > 1;

                            // Fallback: dung segMapping chi khi KHONG co semanticMatchResult
                            const isMissing = matchMapping ? isUnmatched : (segMapping?.isMissing || (segMapping?.humanIndices?.length === 0));

                            return (
                                <div key={idx} className="grid grid-cols-4 group hover:bg-slate-50 border-b border-slate-100">
                                    <div className="p-4 border-r border-slate-100 flex gap-3">
                                        <span className="shrink-0 w-5 h-5 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center text-[9px] font-black border border-slate-200">
                                            {idx + 1}
                                        </span>
                                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap flex-1">{highlightTermsInText(sourceSegments[idx] || '-', sourceTerms, 'bg-amber-100 text-amber-800')}</p>
                                    </div>
                                    <div className="p-4 border-r border-slate-100 bg-indigo-50/30">
                                        <p className="text-sm text-indigo-900 leading-relaxed whitespace-pre-wrap">{highlightTermsInText(aiOutputSegments[idx] || '-', targetTerms, 'bg-emerald-100 text-emerald-800')}</p>
                                    </div>
                                    <div className={`p-4 border-r border-slate-100 ${isMissing ? 'bg-red-50/50' : matchedHumanText ? 'bg-emerald-50/30' : 'bg-amber-50/50'}`}>
                                        {isMissing ? (
                                            <div className="flex items-center gap-2">
                                                <span className="shrink-0 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-red-100 text-red-600 border border-red-200">
                                                    MISSING
                                                </span>
                                            </div>
                                        ) : matchedHumanText ? (
                                            <div className="flex flex-col gap-1">
                                                {isMultiMatch && (
                                                    <div className="flex items-center gap-1 mb-1">
                                                        <span className="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-emerald-200 text-emerald-800">
                                                            Ghep {matchedIndices.map(i => i + 1).join(';')}
                                                        </span>
                                                    </div>
                                                )}
                                                <p className="text-sm text-emerald-900 leading-relaxed whitespace-pre-wrap">
                                                    {matchedHumanText}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <span className="shrink-0 w-4 h-4 rounded-full bg-amber-200 flex items-center justify-center">
                                                    <span className="text-amber-700 text-[10px] font-bold">!</span>
                                                </span>
                                                <span className="text-xs text-amber-700 italic">Khong co benchmark tuong ung</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className={`p-4 ${segMapping?.score !== undefined && segMapping.score < 7 ? 'bg-red-50' : isMissing ? 'bg-red-50/30' : 'bg-slate-50/50'}`}>
                                        <div className="flex items-start gap-2">
                                            {segMapping?.score !== undefined && (
                                                <span className={`shrink-0 text-[10px] font-black px-1.5 py-0.5 rounded ${segMapping.score >= 8 ? 'bg-emerald-100 text-emerald-700' :
                                                    segMapping.score >= 6 ? 'bg-amber-100 text-amber-700' :
                                                        'bg-red-100 text-red-700'
                                                    }`}>{segMapping.score}</span>
                                            )}
                                            <div className={`text-xs leading-relaxed ${segMapping?.score !== undefined && segMapping.score < 7 ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
                                                <div className="flex flex-col gap-1.5 mt-0.5">
                                                    {/* Show MISSING indicator in notes column */}
                                                    {isMissing ? (
                                                        <div className="flex flex-col gap-1 text-xs">
                                                            <span className="text-slate-400 font-medium">N/A</span>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {/* Render Rules */}
                                                            {segMapping?.rules && segMapping.rules.split(';').map(s => s.trim()).filter(Boolean).filter(s => s !== 'N/A').map((text, i) => (
                                                                <div key={`rule-${i}`} className="flex flex-col gap-1 text-xs">
                                                                    <span className="shrink-0 w-fit px-1.5 py-0.5 rounded text-[10px] font-bold uppercase text-blue-700 bg-blue-100">Rule</span>
                                                                    <span className="text-slate-700 pl-1">{text}</span>
                                                                </div>
                                                            ))}

                                                            {/* Render Errors */}
                                                            {segMapping?.errors && segMapping.errors.split(';').map(s => s.trim()).filter(Boolean).filter(s => s !== 'N/A').map((text, i) => (
                                                                <div key={`error-${i}`} className="flex flex-col gap-1 text-xs">
                                                                    <span className="shrink-0 w-fit px-1.5 py-0.5 rounded text-[10px] font-bold uppercase text-red-700 bg-red-100">Loi</span>
                                                                    <span className="text-red-800 font-medium pl-1">{text}</span>
                                                                </div>
                                                            ))}

                                                            {/* Render Reviews */}
                                                            {segMapping?.reviews && segMapping.reviews.split(';').map(s => s.trim()).filter(Boolean).filter(s => s !== 'N/A').map((text, i) => (
                                                                <div key={`review-${i}`} className="flex flex-col gap-1 text-xs">
                                                                    <span className="shrink-0 w-fit px-1.5 py-0.5 rounded text-[10px] font-bold uppercase text-orange-700 bg-orange-100">Xem lai</span>
                                                                    <span className="text-slate-700 pl-1">{text}</span>
                                                                </div>
                                                            ))}

                                                            {/* Render Notes */}
                                                            {segMapping?.notes && segMapping.notes.split(';').map(s => s.trim()).filter(Boolean).filter(s => s !== 'N/A').map((text, i) => (
                                                                <div key={`note-${i}`} className="flex flex-col gap-1 text-xs">
                                                                    <span className="shrink-0 w-fit px-1.5 py-0.5 rounded text-[10px] font-bold uppercase text-purple-700 bg-purple-100">Note</span>
                                                                    <span className="text-purple-800 font-medium pl-1">{text}</span>
                                                                </div>
                                                            ))}

                                                            {/* Fallback for perfect segments */}
                                                            {(!segMapping?.rules && !segMapping?.errors && !segMapping?.reviews && !segMapping?.notes) && (
                                                                <span className="text-slate-400 text-xs italic">OK</span>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default Step4Report;
